/**
 * 与 Native 同步 H5 界面语言（对齐 domi-ai-home）。
 *
 * - Native 就绪后主动 callHandler("getH5Language")，H5 经 responseCallback 回 { locale }
 * - 用户切换语言时 H5 callHandler("setLanguage", { locale })
 *
 * 仅 App WebView；普通浏览器不注册、不调用。
 * 嵌套 iframe 内 setLanguage 转发到顶层；getH5Language 仅在顶层注册。
 */

import { type BridgeLocale, resolveBridgeLocale, toBridgeLocale } from './bridge-copy';
import { invokeBridgeCall, shouldRelayBridgeToTop } from './bridge-relay';
import { hasBridge, waitForJavascriptBridge } from './bridge-ready';
import { isNativeAppContainer } from './native-env';

const LOG_PREFIX = '[bridge:language]';

export type NativeLanguageParams = {
  locale: BridgeLocale;
};

type LanguageBridge = {
  registerHandler: (
    event: string,
    cb: (payload: unknown, responseCallback?: (data: NativeLanguageParams) => void) => void,
  ) => void;
  callHandler: (action: string, params: unknown) => void;
};

let handlerRegistered = false;

function currentLanguageParams(locale?: BridgeLocale | string): NativeLanguageParams {
  if (typeof locale === 'string' && locale !== 'zh' && locale !== 'en') {
    return { locale: toBridgeLocale(locale) };
  }
  return { locale: resolveBridgeLocale(locale as BridgeLocale | undefined) };
}

function asLanguageBridge(
  bridge: {
    registerHandler: (event: string, cb: (payload: unknown) => void) => void;
    callHandler: (action: string, params: unknown) => void;
  } | null,
): LanguageBridge | null {
  return bridge as LanguageBridge | null;
}

/** Native 拉取当前语言：尽早 register，Bridge 注入后再挂（仅顶层）。 */
export function registerNativeLanguageHandlers(): void {
  if (typeof window === 'undefined' || handlerRegistered) return;
  // iframe 内不要注册：Native 只会问顶层
  if (shouldRelayBridgeToTop()) return;

  const attach = (raw: LanguageBridge | null) => {
    if (!raw || handlerRegistered) return;
    handlerRegistered = true;
    raw.registerHandler('getH5Language', (_payload, responseCallback) => {
      const params = currentLanguageParams();
      console.log(LOG_PREFIX, 'getH5Language', params);
      responseCallback?.(params);
    });
    console.log(LOG_PREFIX, 'registered getH5Language');
  };

  if (isNativeAppContainer()) {
    void waitForJavascriptBridge(30_000).then((bridge) => attach(asLanguageBridge(bridge)));
    return;
  }

  document.addEventListener(
    'miaobi-jsbridge-ready',
    () => {
      void waitForJavascriptBridge(5_000).then((bridge) => attach(asLanguageBridge(bridge)));
    },
    { once: true },
  );
}

/** 用户切换语言时通知 Native；非 App 直接 return。 */
export function syncNativeLocale(locale?: BridgeLocale | string): void {
  if (typeof window === 'undefined') return;
  if (!hasBridge() && !isNativeAppContainer()) return;

  const params = currentLanguageParams(locale);
  void invokeBridgeCall({
    action: 'setLanguage',
    params,
    resultEvent: null,
    timeoutMs: 15_000,
  })
    .then(() => {
      console.log(LOG_PREFIX, 'setLanguage', params);
    })
    .catch((err) => {
      console.warn(LOG_PREFIX, 'setLanguage failed', err);
    });
}
